import {
  Component, OnInit, Input, Inject, ViewEncapsulation, ChangeDetectorRef,
  TemplateRef
} from '@angular/core';
import { ViewportScroller } from '@angular/common';
import { ActivatedRoute, Router } from '@angular/router';
import { UntypedFormGroup } from '@angular/forms';
import { zip } from 'rxjs';
import isEmpty from 'lodash-es/isEmpty';

import { isNullOrUndefined } from '@app/util/util';
import { IS_BROWSER } from '@app/modules/core/tokens/app.tokens';
import {
  AirValidationBySectorResponse,
  AirValidationResponse,
  AirValidationResponseVm,
  TripOptions
} from '@app/entities/air-validation.response';
import { TravelDetailProvider } from '@shared/flight-itinerary/entities/travel-detail-provider';
import { BookingFacade } from '@booking/booking.facade';
import { TravelerTypes } from '@pax/entities/traveler-info';
import { BillingFacade } from '@billing/billing.facade';
import { PaxFacade } from '@pax/pax.facade';
import { PaxFormService } from '@pax/services/pax-form.service';
import { TravelBookingType } from '@pax/entities/travel-booking-types';
import { ScrollFormService } from '@reservation/services/scroll-form.service';
import { PropertiesAmplitudeService } from '@app/services/properties-amplitude.service';
import { LuggagePipe } from '@shared/luggage/pipes/luggage.pipe';
import { BookingAmplitudeService } from '@booking/services/booking-amplitude.service';
import { TriggerAmplitudeService } from '@amplitude/services/trigger-amplitude.service';
import { FlightItem } from '@app/entities/flight-results';
import { AppFacade } from '@app/app.facade';
import { BookingFlightsBaseContainer } from '@booking/abstracts/booking-flights-base.container';
import { AssistanceFacade } from '@shared/travel-assistance/assistance.facade';
import { FullValidTravelersPipe } from '@pax/pipes/full-valid-travelers.pipe';
import { TravelerListVmPipe } from '@pax/pipes/traveler-list-vm.pipe';
import { AvoService } from '@avo/services/avo.service';
import { flightFreeCancellationBookingEnabled } from '@ux/mappers/free-cancellation.mapper';
import { SearchBy } from '@shared/base-search-bar/entities/search.entities';

@Component({
  selector: 'app-booking-flights-by-sector',
  templateUrl: './booking-flights-by-sector.container.html',
  styleUrls: ['../booking-base.container.sass'],
  encapsulation: ViewEncapsulation.ShadowDom,
  standalone: false
})
export class BookingFlightsBySectorContainer extends BookingFlightsBaseContainer implements OnInit {

  @Input() airValidationBySector: AirValidationBySectorResponse;
  @Input() passengerTitleBlock!: TemplateRef<void>;

  public bookingFlightsForm: UntypedFormGroup;
  public flightSummaryProvider: TravelDetailProvider = TravelDetailProvider.BOOKING_FLIGHT;
  public adults: TravelerTypes = TravelerTypes.ADULTS;
  public children: TravelerTypes = TravelerTypes.CHILDREN;
  public infalts: TravelerTypes = TravelerTypes.INFANTS;
  public policiesType: SearchBy = SearchBy.FLIGHTS_BY_SECTOR;
  public selectedRoutes: FlightItem[] = [];

  constructor(
    private readonly luggagePipe: LuggagePipe,
    protected route: ActivatedRoute,
    protected router: Router,
    protected cdRef: ChangeDetectorRef,
    protected appFacade: AppFacade,
    protected viewportScroller: ViewportScroller,
    protected facade: BookingFacade,
    protected billingFacade: BillingFacade,
    protected paxFacade: PaxFacade,
    protected paxFormService: PaxFormService,
    protected scrollFormService: ScrollFormService,
    protected propertiesAmplitudeService: PropertiesAmplitudeService,
    protected bookingAmplitudeService: BookingAmplitudeService,
    protected triggerAmplitudeService: TriggerAmplitudeService,
    protected assistanceFacade: AssistanceFacade,
    protected fullValidTravelersPipe: FullValidTravelersPipe,
    protected avoService: AvoService,
    protected travelerListVmPipe: TravelerListVmPipe,
    @Inject(IS_BROWSER) public isBrowser: boolean
  ) {
    super(
      route, router, cdRef, appFacade, facade, viewportScroller, billingFacade, paxFacade, paxFormService, scrollFormService,
      propertiesAmplitudeService, bookingAmplitudeService, triggerAmplitudeService,
      assistanceFacade, fullValidTravelersPipe, avoService, travelerListVmPipe
    );
    this.travelerBookingProvider = TravelBookingType.FLIGHTS_BY_SECTOR;
  }

  ngOnInit(): void {
    this.mappedValidationID = this.airValidationBySector.AirValidateResponse.map(trip => trip.ValidationId);
    this.amplitudeValidationResponse = this.airValidationBySector.AirValidateResponse[0];
    if (this.isBrowser) {
      super.ngOnInit();

      this.configDefaultByInfoType(this.travelerBookingProvider, this.startDate);
      this.listenToAssistanceSearchCall(
        this.airValidationBySector,
        this.airValidationBySector?.AirValidateResponse[0].TripOption.Price?.CurrencyCode,
        this.route.snapshot.paramMap
      );
    }
  }

  get hasAirValidation(): boolean {
    return !isEmpty(this.airValidationBySector);
  }

  get firstTrip(): TripOptions {
    return this.airValidationBySector?.AirValidateResponse[0]?.TripOption;
  }

  get airlineCode(): string {
    return this.firstTrip?.Sectors[0]?.Flights[0]?.AirlineCode;
  }

  get airlineProvider(): string {
    return this.firstTrip?.Provider;
  }

  get startDate(): string {
    return this.firstTrip?.Sectors?.[0]?.Flights?.[0]?.DepartureDate;
  }

  get showBySectorItineraryInfo(): boolean {
    return this.airValidationBySector?.AirValidateResponse.length > 1;
  }

  get airValidation(): AirValidationResponse {
    return this.airValidationBySector?.AirValidateResponse?.[0];
  }

  get freeCancellationValidate(): boolean {
    return this.airValidationBySector?.AirValidateResponse?.some(airValidation =>
      flightFreeCancellationBookingEnabled(airValidation, this.airlineProvider));
  }

  public showFlightDetail(data: { validator: AirValidationResponseVm, sectorNumber: number }): void {
    zip(
      this.passengerArrangementsState$,
      this.flightClass$,
    ).subscribe(([passengerArrangementsState, flightClass]) => {
      this.router.navigate([this.bookingDetail],
        {
          state: {
            passengerArrangement: passengerArrangementsState,
            price: data.validator.TripOption.Price,
            flightClass: flightClass,
            provider: data.validator.TripOption.Provider,
            airValidationSector: data.validator.TripOption.Sectors,
            sectorSelected: data.sectorNumber,
            productReference: data.validator.TripOption.ProductReference,
          },
          relativeTo: this.route, skipLocationChange: true
        }
      );
    }).unsubscribe();
  }

  public baggage(tripOption: AirValidationResponse): boolean {
    if (this.validateBaggage(tripOption)) {
      const baggage = this.luggagePipe.transform(tripOption?.TripOption?.Sectors[0].Flights[0].BaggageRestrictions);
      return !isNullOrUndefined(baggage) && baggage !== 0 ? true : false;
    } else return false;
  }

  public fareFamilies(tripOption: AirValidationResponse): string | null {
    if (tripOption?.TripOption?.Sectors?.length <= 0) {
      return null;
    }
    let fareFamilyText = [];
    tripOption?.TripOption?.Sectors.forEach(sector => {
      if (!fareFamilyText.includes(sector.Flights[0]?.FareFamilies)) {
        fareFamilyText.push(sector.Flights[0]?.FareFamilies)
      }
    })
    return fareFamilyText.join(', ');
  }

  private validateBaggage(tripOption: AirValidationResponse): boolean {
    return !!tripOption?.TripOption?.Sectors?.[0]?.Flights?.[0]?.BaggageRestrictions;
  }
}
